import type {AggregationSurveyListItem} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {DataType, DataTypeOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import {
    SurveyAnswerSentiment,
    SurveyAttentionType,
    SurveyReviewState
} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {SURVEY_REVIEW_STATE_LABEL_KEYS} from "../../common/uiElements/SurveyReviewStateElement";
import {useGuestCategories} from "../../guestCategories/useGuestCategories";
import {useSurveyFeedbackThemes} from "../shared/useSurveyFeedbackThemes";
import {SURVEY_ATTENTION_TYPE_LABEL_KEYS} from "./SurveySubmissionCells";

const SENTIMENT_LABEL_KEYS: Record<SurveyAnswerSentiment, string> = {
    [SurveyAnswerSentiment.POSITIVE]: 'Positive',
    [SurveyAnswerSentiment.NEUTRAL]: 'Neutral',
    [SurveyAnswerSentiment.NEGATIVE]: 'Negative',
};

const SENTIMENTS = Object.values(SurveyAnswerSentiment);
const REVIEW_STATES = Object.values(SurveyReviewState);
const ATTENTION_TYPES = Object.values(SurveyAttentionType);

interface Args {
    surveys: Pick<AggregationSurveyListItem, 'id' | 'name'>[]
    /** The host owns a date control, so the period is no "Submitted" filter rule. */
    hasOwnPeriod: boolean
    /** The versions of the one survey the list is scoped to; 0 while it shows several. */
    singleSurveyVersion: number
}

/** The filters both submissions lists offer, with the theme, sector and guest-category options they list. */
export function useSurveySubmissionFilterOptions({surveys, hasOwnPeriod, singleSurveyVersion}: Args): FilterOption[] {
    const {t} = useTranslation();

    const {themes: feedbackThemes} = useSurveyFeedbackThemes();
    const themes = useMemo(
        () => feedbackThemes.flatMap(
            theme => theme.id ? [{id: theme.id, name: theme.name}] : []
        ),
        [feedbackThemes]
    );

    const allSectors = useAllSectors();
    const sectors = useMemo(
        () => allSectors.flatMap(sector => sector.id ? [{id: sector.id, name: sector.name}] : []),
        [allSectors]
    );

    const {categories: guestCategories} = useGuestCategories();

    const filterOptions = useMemo<FilterOption[]>(() => [
        ...(hasOwnPeriod ? [] : [{
            field: 'submittedAt',
            name: t('Submitted'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.BETWEEN],
            predefinedValues: [],
            dataType: DataType.TIMESTAMP
        } satisfies FilterOption]),
        {
            field: 'reviewState',
            name: t('Review state'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: REVIEW_STATES.map(state => ({
                cid: state,
                name: t(SURVEY_REVIEW_STATE_LABEL_KEYS[state]),
                colorCode: ''
            })),
            enumValues: REVIEW_STATES,
            dataType: DataType.ENUM
        },
        {
            field: 'attentionType',
            name: t('Needs attention'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: ATTENTION_TYPES.map(type => ({
                cid: type,
                name: t(SURVEY_ATTENTION_TYPE_LABEL_KEYS[type]),
                colorCode: ''
            })),
            enumValues: ATTENTION_TYPES,
            dataType: DataType.ENUM
        },
        {
            field: 'surveyId',
            name: t('Survey'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: surveys.map(survey => ({cid: survey.id, name: survey.name || survey.id, colorCode: ''})),
            enumValues: surveys.map(survey => survey.id),
            dataType: DataType.ENUM
        },
        ...(sectors.length ? [{
            field: 'sectorId',
            name: t('Sector'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: sectors.map(sector => ({cid: sector.id, name: sector.name?.trim() || sector.id, colorCode: ''})),
            enumValues: sectors.map(sector => sector.id),
            dataType: DataType.ENUM
        } satisfies FilterOption] : []),
        {
            field: 'sentiment',
            name: t('Sentiment'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: SENTIMENTS.map(sentiment => ({
                cid: sentiment,
                name: t(SENTIMENT_LABEL_KEYS[sentiment]),
                colorCode: ''
            })),
            enumValues: SENTIMENTS,
            dataType: DataType.ENUM
        },
        ...(themes.length ? [{
            field: 'themeId',
            name: t('Survey theme'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: themes.map(theme => ({cid: theme.id, name: theme.name?.trim() || theme.id, colorCode: ''})),
            enumValues: themes.map(theme => theme.id),
            dataType: DataType.ENUM
        } satisfies FilterOption] : []),
        ...(guestCategories.length ? [{
            field: 'guestCategoryId',
            name: t('Guest category'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: guestCategories.map(category => ({
                cid: category.id!,
                name: category.name?.trim() || category.id!,
                colorCode: ''
            })),
            enumValues: guestCategories.map(category => category.id!),
            dataType: DataType.ENUM
        } satisfies FilterOption] : []),
        {
            field: 'reviewedById',
            name: t('Resolved by'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: [],
            dataType: DataType.TEXT,
            idType: 'user'
        },
        {
            field: 'reviewedAt',
            name: t('Resolved at'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.BETWEEN],
            predefinedValues: [],
            dataType: DataType.TIMESTAMP
        },
        {
            field: 'language',
            name: t('Language'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: [],
            dataType: DataType.TEXT,
            idType: 'language_code_iso_639_1'
        }
    ], [guestCategories, hasOwnPeriod, sectors, surveys, t, themes]);

    const tableFilterOptions = useMemo<FilterOption[]>(() => {
        if (singleSurveyVersion < 1) return filterOptions;

        const versions = Array.from({length: singleSurveyVersion}, (_, index) => String(index + 1)).reverse();

        return [...filterOptions, {
            field: 'surveyVersion',
            name: t('Version'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: versions.map(version => ({cid: version, name: t('v{{n}}', {n: version}), colorCode: ''})),
            enumValues: versions,
            dataType: DataType.ENUM
        }];
    }, [filterOptions, singleSurveyVersion, t]);

    return tableFilterOptions;
}
